import { useState } from 'react'
import {
  History, Package, CreditCard, Calendar, ExternalLink,
  Copy, Check, Key, Gift
} from 'lucide-react'
import { motion } from 'framer-motion'
import type { Transaction } from '../../modules/order/entities/Order'

interface GiftItem {
  variant_id: string
  variant_name?: string
  product_name?: string
  quantity: number
}

// Extended interface to include joined fields from backend
interface TransactionWithDetails extends Omit<Transaction, 'metadata'> {
  metadata?: {
    guide_url?: string
    keys?: string[]
    quantity_in_order?: number
    order_index?: number
    payment_method?: string
    transfer_content?: string
    is_manual_delivery?: boolean
    is_admin_adjustment?: boolean
    note?: string
    combo_name?: string
    gift_items?: GiftItem[]
    deposit_bonus_amount?: number
    deposit_bonus_percent?: number
    deposit_bonus_min_deposit?: number
    deposit_bonus_reason?: string
    credited_amount?: number
  }
  product_name?: string
  variant_name?: string
  duration_days?: number
  guide_url?: string
  key_value?: string
  product_variants?: {
    name: string
    guide_url?: string
    products?: {
      name: string
      guide_url?: string
      account_info?: string
    }
  }
  product_keys?: {
    key_value: string
  }
}

interface TransactionHistoryProps {
  transactions: TransactionWithDetails[]
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
} as const

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100
    }
  }
} as const

export default function TransactionHistory({ transactions }: TransactionHistoryProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedKey(id)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-12">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <History className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500">Chưa có giao dịch nào</p>
        </motion.div>
      </div>
    )
  }

  return (
    <motion.div
      className="space-y-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {transactions.map((tx) => {
        const txType = String(tx.type || '')
        const isTopUp = txType === 'top_up' || txType === 'topup'
        const productName = tx.product_name || tx.product_variants?.products?.name || 'Sản phẩm'
        const variantName = tx.variant_name || tx.product_variants?.name || 'Gói'
        const guideUrl = tx.guide_url || tx.metadata?.guide_url || tx.product_variants?.guide_url || tx.product_variants?.products?.guide_url
        const quantityInOrder = tx.metadata?.quantity_in_order
        const orderIndex = tx.metadata?.order_index
        const txCode = typeof tx.id === 'string' && tx.id ? tx.id.split('-')[0].toUpperCase() : ''

        const metaKeys = tx.metadata?.keys
        const firstMetaKey = Array.isArray(metaKeys) && metaKeys.length ? String(metaKeys[0] ?? '') : ''
        const displayKey = tx.key_value || tx.product_keys?.key_value || firstMetaKey || tx.product_variants?.products?.account_info || 'N/A'
        const topupAmount = Math.max(0, Number(tx.amount || 0))
        const bonusAmount = Math.max(0, Number(tx.metadata?.deposit_bonus_amount || 0))
        const creditedAmount = Math.max(
          0,
          Number(tx.metadata?.credited_amount || (isTopUp ? (topupAmount + bonusAmount) : Math.abs(tx.amount || 0)))
        )
        const bonusPercentRaw = Math.max(0, Number(tx.metadata?.deposit_bonus_percent || 0))
        const bonusPercent = bonusPercentRaw > 0
          ? bonusPercentRaw
          : (topupAmount > 0 && bonusAmount > 0 ? Math.floor((bonusAmount * 100) / topupAmount) : 0)
        const bonusReason = String(tx.metadata?.deposit_bonus_reason || '').trim()
        const amountToDisplay = isTopUp
          ? (tx.status === 'completed' ? creditedAmount : topupAmount)
          : Math.abs(tx.amount)

        return (
          <motion.div
            key={tx.id}
            variants={itemVariants}
            layout
            className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors hover:border-blue-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm"
          >
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              {/* Left Side - Main Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {tx.type === 'purchase' ? (
                    <div className="p-1.5 bg-purple-100 rounded-full">
                      <Package className="h-4 w-4 text-purple-600" />
                    </div>
                  ) : (
                    <div className="p-1.5 bg-green-100 rounded-full">
                      <CreditCard className="h-4 w-4 text-green-600" />
                    </div>
                  )}
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {tx.type === 'purchase'
                      ? (tx.metadata?.is_manual_delivery
                        ? `Mã giao dịch - ${productName} - ${variantName}`
                        : `${productName} - ${variantName}`)
                      : (tx.metadata?.is_admin_adjustment ? (tx.metadata?.note || 'Điều chỉnh số dư') : 'Nạp tiền vào tài khoản')
                    }
                  </span>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${tx.status === 'completed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : tx.status === 'processing'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : tx.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 animate-pulse'
                          : tx.status === 'refunded'
                            ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}
                  >
                    {tx.status === 'completed'
                      ? 'Thành công'
                      : tx.status === 'processing'
                        ? 'Đang xử lý'
                        : tx.status === 'pending'
                          ? 'Chờ xử lý'
                          : tx.status === 'refunded'
                            ? 'Hoàn tiền'
                            : 'Thất bại'}
                  </span>
                  {quantityInOrder && quantityInOrder > 1 && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                      #{orderIndex}/{quantityInOrder}
                    </span>
                  )}
                </div>

                <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1.5">
                  <p className="flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>
                      {new Date(tx.created_at).toLocaleString('vi-VN')}
                    </span>
                  </p>

                  <p className="text-gray-600 dark:text-gray-300">
                    <span className="text-gray-500 dark:text-gray-500">Mã giao dịch:</span>{' '}
                    <span
                      className="font-mono font-medium text-gray-700 dark:text-gray-200 select-all cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-1 -ml-1 transition-colors"
                      onClick={() => copyToClipboard(txCode || tx.id, tx.id + '-txid')}
                      title="Click để copy"
                    >
                      {txCode || tx.id}
                    </span>
                    {copiedKey === tx.id + '-txid' && (
                      <span className="ml-2 text-green-500 text-xs font-bold animate-fade-in">Đã copy!</span>
                    )}
                  </p>

                  {tx.type === 'purchase' && tx.product_variants && (
                    <p className="text-gray-600 dark:text-gray-300">
                      <span className="text-gray-500 dark:text-gray-500">Gói:</span>{' '}
                      <span className="font-medium">{variantName}</span>
                    </p>
                  )}

                  {isTopUp && tx.metadata?.payment_method === 'bank_transfer' && tx.metadata?.transfer_content && (
                    <div className="flex items-start gap-2 mt-2">
                      <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-700 w-fit">
                        <span className="text-xs text-gray-500 block mb-0.5">Nội dung chuyển khoản:</span>
                        <code className="font-mono text-sm font-bold text-blue-600 dark:text-blue-400">{tx.metadata.transfer_content}</code>
                      </div>
                    </div>
                  )}
                </div>

                {isTopUp && (
                  <div className="mt-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 border border-emerald-100 dark:border-emerald-800/40">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-300">Tiền nạp</span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{topupAmount.toLocaleString('vi-VN')}đ</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-gray-600 dark:text-gray-300">Thưởng nạp</span>
                      <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                        +{bonusAmount.toLocaleString('vi-VN')}đ{bonusPercent > 0 ? ` (${bonusPercent}%)` : ''}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1 pt-1 border-t border-emerald-100 dark:border-emerald-800/40">
                      <span className="font-semibold text-gray-700 dark:text-gray-200">Tổng cộng vào ví</span>
                      <span className="font-bold text-emerald-700 dark:text-emerald-300">{creditedAmount.toLocaleString('vi-VN')}đ</span>
                    </div>
                    {bonusReason ? (
                      <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">Lý do: {bonusReason}</p>
                    ) : null}
                  </div>
                )}

                {/* Guide URL */}
                {tx.type === 'purchase' && guideUrl && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-3 flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 border border-amber-100 dark:border-amber-800/50"
                  >
                    <ExternalLink className="h-4 w-4 text-amber-600 dark:text-amber-500 flex-shrink-0" />
                    <a
                      href={guideUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-amber-600 dark:text-amber-500 hover:text-amber-800 dark:hover:text-amber-300 hover:underline truncate"
                    >
                      Xem hướng dẫn sử dụng
                    </a>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => copyToClipboard(guideUrl, tx.id + '-guide')}
                      className="ml-auto text-gray-400 hover:text-amber-600 p-1 flex-shrink-0"
                      title="Sao chép liên kết hướng dẫn"
                    >
                      {copiedKey === tx.id + '-guide' ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </motion.button>
                  </motion.div>
                )}

                {/* Combo Gift Items */}
                {tx.type === 'purchase' && tx.metadata?.combo_name && (
                  <div className="mt-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800/50">
                    <div className="flex items-start gap-2">
                      <Gift className="h-4 w-4 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-purple-900 dark:text-purple-200 text-sm">🎁 Combo quà tặng: {tx.metadata.combo_name}</h4>
                        {tx.metadata.gift_items && tx.metadata.gift_items.length > 0 && (
                          <ul className="mt-1.5 space-y-0.5">
                            {tx.metadata.gift_items.map((gift: GiftItem, idx: number) => (
                              <li key={idx} className="text-xs text-purple-700 dark:text-purple-300 flex items-center">
                                <span className="mr-1.5">•</span>
                                <span>
                                  {gift.product_name ? `${gift.product_name} - ` : ''}{gift.variant_name || 'Sản phẩm'} x{gift.quantity}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Amount & Key */}
              <div className="flex flex-col items-end gap-2 min-w-[180px]">
                <span className={`text-lg font-bold ${tx.type === 'purchase' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {tx.type === 'purchase' ? '-' : '+'}
                  {amountToDisplay.toLocaleString('vi-VN')}đ
                </span>

                {tx.type === 'purchase' && (
                  <>
                    {tx.metadata?.is_manual_delivery ? (
                      <div className="flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 rounded-lg border border-yellow-200 dark:border-yellow-800/50 w-full">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-yellow-700 dark:text-yellow-400 mb-0.5">
                            {productName} - {variantName}:
                          </p>
                          <code className="text-sm font-mono text-yellow-900 dark:text-yellow-200 font-bold block truncate">
                            {txCode || tx.id}
                          </code>
                          <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">Gửi mã này cho Admin để nhận key</p>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => {
                            const copyText = `${txCode || tx.id} - ${productName} - ${variantName}`
                            copyToClipboard(copyText, tx.id)
                          }}
                          className="text-yellow-500 hover:text-yellow-700 dark:hover:text-yellow-300 p-1 flex-shrink-0"
                          title="Sao chép Mã giao dịch"
                        >
                          {copiedKey === tx.id ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </motion.button>
                      </div>
                    ) : (
                      (() => {
                        return (
                          <div className="flex items-center gap-2 bg-gradient-to-r from-gray-50 to-purple-50 dark:from-gray-800 dark:to-purple-900/20 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 w-full shadow-sm">
                            <Key className="h-4 w-4 text-purple-500 dark:text-purple-400 flex-shrink-0" />
                            <code className="text-sm font-mono text-gray-800 dark:text-gray-200 truncate flex-1">
                              {displayKey}
                            </code>
                            {displayKey !== 'N/A' && (
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => copyToClipboard(displayKey, tx.id)}
                                className="text-gray-500 hover:text-amber-600 dark:text-gray-400 dark:hover:text-amber-400 transition-colors flex-shrink-0"
                                title="Sao chép Key"
                              >
                                {copiedKey === tx.id ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </motion.button>
                            )}
                          </div>
                        )
                      })()
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )
      })}
    </motion.div>
  )
}
